<template>
  <form ref="form" novalidate v-bind="$attrs" @submit="onSubmit" @reset="handleFormReset">
    <slot v-bind="slotProps()"></slot>
  </form>
</template>

<script
  setup
  lang="ts"
  generic="TValues extends GenericObject = GenericObject, TOutput extends GenericObject = TValues"
>
import {
  useForm,
  type GenericObject,
  type InvalidSubmissionHandler,
  type SubmissionHandler,
  type TypedSchema
} from 'vee-validate'
import { toRef, useAttrs, useTemplateRef } from 'vue'

function isCallable(fn: unknown): fn is (...args: any[]) => any {
  return typeof fn === 'function'
}

function isFormSubmitEvent(evt: unknown): evt is Event & { target: HTMLFormElement } {
  return isEvent(evt) && (evt as any).target && 'submit' in (evt as any).target
}

function isEvent(evt: unknown): evt is Event {
  if (!evt) return false
  if (typeof Event !== 'undefined' && isCallable(Event) && evt instanceof Event) {
    return true
  }
  return false
}

const props = defineProps<{
  validationSchema?: TypedSchema<TValues, TOutput>
  onSubmit?: SubmissionHandler<TValues, TOutput>
  onInvalidSubmit?: InvalidSubmissionHandler<TValues, TOutput>
}>()

const attrs = useAttrs()
const formRef = useTemplateRef('form')

const validationSchema = toRef(props, 'validationSchema')

const {
  errors,
  errorBag,
  values,
  meta,
  isSubmitting,
  isValidating,
  submitCount,
  controlledValues,
  validate,
  validateField,
  handleReset,
  resetForm,
  handleSubmit,
  setErrors,
  setFieldError,
  setFieldValue,
  setValues,
  setFieldTouched,
  setTouched,
  resetField
} = useForm<TValues, TOutput>({
  validationSchema: validationSchema.value ? validationSchema : undefined
})

const submitForm = handleSubmit((_, { evt }) => {
  if (isFormSubmitEvent(evt)) {
    evt.target.submit()
  }
}, props.onInvalidSubmit)

const onSubmit = props.onSubmit ? handleSubmit(props.onSubmit, props.onInvalidSubmit) : submitForm

function handleFormReset(e?: Event) {
  if (isEvent(e)) {
    e.preventDefault()
  }

  handleReset()
  if (typeof attrs.onReset === 'function') {
    attrs.onReset()
  }
}

function handleScopedSlotSubmit(
  evt: Event | SubmissionHandler<GenericObject>,
  onSubmit?: SubmissionHandler<GenericObject>
) {
  const onSuccess = typeof evt === 'function' && !onSubmit ? evt : onSubmit

  return handleSubmit(
    onSuccess as SubmissionHandler<GenericObject>,
    props.onInvalidSubmit
  )(evt as Event)
}

function getValues() {
  return values
}

function getMeta() {
  return meta.value
}

function getErrors() {
  return errors.value
}

function requestSubmit() {
  formRef.value?.requestSubmit()
}

const slotProps = () => ({
  meta: meta,
  errors: errors,
  errorBag: errorBag,
  values,
  isSubmitting: isSubmitting,
  isValidating: isValidating,
  submitCount: submitCount,
  controlledValues: controlledValues,
  validate,
  validateField,
  handleSubmit: handleScopedSlotSubmit,
  handleReset,
  submitForm,
  setErrors,
  setFieldError,
  setFieldValue,
  setValues,
  setFieldTouched,
  setTouched,
  resetForm,
  resetField,
  getValues,
  getMeta,
  getErrors,
  requestSubmit
})

defineExpose({
  setFieldError,
  setErrors,
  setFieldValue,
  setValues,
  setFieldTouched,
  setTouched,
  resetForm,
  validate,
  validateField,
  resetField,
  getValues,
  getMeta,
  getErrors,
  requestSubmit,
  values,
  meta,
  errors
})
</script>
